// Enhanced useCart hook with stock validation
import { useState, useEffect, useContext, createContext } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Helper function to extract stock from API response
const getProductStock = (productData) => {
  return productData.data?.product?.stock || 0;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { apiRequest } = useAuth();

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(parsedCart);
        validateCartStock(parsedCart);
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Validate stock for all cart items
  const validateCartStock = async (items = cartItems) => {
    if (items.length === 0) return;

    setLoading(true);
    try {
      const updatedItems = await Promise.all(
        items.map(async (item) => {
          try {
            const response = await apiRequest(`/products/${item.id}`);
            if (response.ok) {
              const productData = await response.json();
              const currentStock = getProductStock(productData);
              
              return {
                ...item,
                stock: currentStock,
                quantity: Math.min(item.quantity, currentStock)
              };
            }
            return item;
          } catch (error) {
            console.error(`Error checking stock for ${item.name}:`, error);
            return item;
          }
        })
      );

      // Remove items with zero stock
      const availableItems = updatedItems.filter(item => item.stock > 0);
      
      if (availableItems.length !== items.length) {
        console.log('Some items were removed due to insufficient stock');
      }

      setCartItems(availableItems);
    } catch (error) {
      console.error('Error validating cart stock:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add item to cart with stock validation
  const addToCart = async (product, quantity = 1) => {
    try {
      console.log('🔍 Adding to cart - Product:', product);
      console.log('🔍 Adding to cart - Quantity:', quantity);
      
      const productId = product.id || product._id;
      const stockCheckUrl = `/products/${productId}`;
      
      const response = await apiRequest(stockCheckUrl);
      console.log('🔍 Stock check response:', response);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Stock check failed:', errorText);
        throw new Error('Failed to check product availability');
      }

      const productData = await response.json();
      console.log('🔍 Full API response:', productData);
      
      const currentStock = getProductStock(productData);
      console.log('🔍 Extracted stock value:', currentStock);

      if (currentStock < quantity) {
        console.error('❌ Insufficient stock:', { currentStock, requestedQuantity: quantity });
        throw new Error(`Only ${currentStock} items available in stock`);
      }

      const existingItem = cartItems.find(item => item.id === productId);

      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        if (newQuantity > currentStock) {
          throw new Error(`Cannot add more items. Only ${currentStock} available in stock`);
        }
        updateQuantity(productId, newQuantity);
      } else {
        const newItem = {
          id: productId,
          name: product.name,
          price: product.price,
          image: product.images?.[0] || product.image,
          category: product.category,
          quantity: quantity,
          stock: currentStock
        };
        
        console.log('✅ Adding new item to cart:', newItem);
        setCartItems(prev => [...prev, newItem]);
      }
    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      throw error;
    }
  };

  // Update quantity with stock validation
  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    try {
      const response = await apiRequest(`/products/${productId}`);
      if (response.ok) {
        const productData = await response.json();
        const currentStock = getProductStock(productData);
        
        if (newQuantity > currentStock) {
          throw new Error(`Only ${currentStock} items available in stock`);
        }

        setCartItems(prev =>
          prev.map(item =>
            item.id === productId
              ? { ...item, quantity: newQuantity }
              : item
          )
        );
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    }
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);
  const totalPrice = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      loading,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      validateCartStock,
      totalItems,
      totalPrice
    }}>
      {children}
    </CartContext.Provider>
  );
};